import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
  Divider,
  useTheme
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Percent as PercentIcon
} from '@mui/icons-material';
import taxService from '../../services/taxService';
import { ITax, ITaxCreate, ITaxUpdate } from '../../types/tax';

type TaxFormProps = {
  mode?: 'create' | 'edit';
};

const TaxForm: React.FC<TaxFormProps> = ({ mode = 'create' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [formData, setFormData] = useState<ITaxCreate>({
    name: '',
    rate: 0,
    description: '',
    isActive: true
  });
  
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(mode === 'edit');
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // Fetch tax data if editing
  useEffect(() => {
    const fetchTax = async () => {
      if (mode === 'edit' && id) {
        setFetchLoading(true);
        try {
          const tax = await taxService.getTaxById(id);
          setFormData({
            name: tax.name,
            rate: tax.rate,
            description: tax.description || '',
            isActive: tax.isActive
          });
          setError(null);
        } catch (err) {
          console.error('Error fetching tax:', err);
          setError('حدث خطأ أثناء جلب بيانات الضريبة');
        } finally {
          setFetchLoading(false);
        }
      }
    };
    
    fetchTax();
  }, [mode, id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let numValue = parseFloat(value);
    
    // Handle NaN case or limit to valid range
    if (name === 'rate') {
      if (isNaN(numValue)) {
        numValue = 0;
      } else if (numValue < 0) {
        numValue = 0;
      } else if (numValue > 100) {
        numValue = 100;
      }
    }
    
    setFormData(prevData => ({
      ...prevData,
      [name]: numValue
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (mode === 'create') {
        await taxService.createTax(formData);
        setSnackbar({
          open: true,
          message: 'تم إنشاء الضريبة بنجاح',
          severity: 'success'
        });
        // Reset form after successful creation
        setFormData({
          name: '',
          rate: 0,
          description: '',
          isActive: true
        });
      } else if (mode === 'edit' && id) {
        const updateData: ITaxUpdate = { ...formData };
        await taxService.updateTax(id, updateData);
        setSnackbar({
          open: true,
          message: 'تم تحديث الضريبة بنجاح',
          severity: 'success'
        });
      }
      setError(null);
    } catch (err) {
      console.error('Error saving tax:', err);
      setError('حدث خطأ أثناء حفظ بيانات الضريبة');
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ البيانات',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = () => {
    navigate('/settings/taxes');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (fetchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
          {mode === 'create' ? 'إضافة ضريبة جديدة' : 'تعديل الضريبة'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleReturn}
        >
          العودة إلى القائمة
        </Button>
      </Box>

      <Card sx={{ mb: 3, boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  name="name"
                  label="اسم الضريبة"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="أدخل اسم الضريبة"
                  variant="outlined"
                  margin="normal"
                  error={formData.name.trim() === ''}
                  helperText={formData.name.trim() === '' ? 'اسم الضريبة مطلوب' : ''}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  name="rate"
                  label="معدل الضريبة (%)"
                  type="number"
                  value={formData.rate}
                  onChange={handleNumberChange}
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    endAdornment: <InputAdornment position="end"><PercentIcon /></InputAdornment>,
                    inputProps: { min: 0, max: 100, step: 0.01 }
                  }}
                  error={formData.rate < 0 || formData.rate > 100}
                  helperText={formData.rate < 0 || formData.rate > 100 ? 'معدل الضريبة يجب أن يكون بين 0 و 100' : ''}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  label="وصف الضريبة"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="أدخل وصفاً اختيارياً للضريبة"
                  variant="outlined"
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleChange}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label="نشط"
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleReturn}
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                disabled={loading || formData.name.trim() === '' || formData.rate < 0 || formData.rate > 100}
              >
                {loading ? <CircularProgress size={24} /> : 'حفظ'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaxForm;